import {listReportIds, REPORT_TYPES, ReportDefinitionId} from '../constants/ReportService';
import {toIsoDateWithTime, formatStartDateUtcWithTimeZone, formatEndDateUtcWithTimeZone} from './date-helpers';
import {ISSUE_FILTER_PROPERTIES, ISSUE_HIDDEN_FILTER_PROPERTIES} from '../constants/issue-query-params';
import i18n from './i18n';
import featureFlags, {FEATURE_FLAGS} from './featureFlags';
import {getSortName, ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';

const simpleValue = filter => filter.map(filterValue => filterValue);
const singleValue = filter => (filter ? [filter] : []);
const dateValue = value => [toIsoDateWithTime(value)];

const startDateValue = dateFilter => dateValue(dateFilter[0]);
const endDateValue = dateFilter => dateValue(dateFilter[1]);
const startInstantValue = dateFilter => [formatStartDateUtcWithTimeZone(dateFilter[0])];
const endInstantValue = dateFilter => [formatEndDateUtcWithTimeZone(dateFilter[1])];
const isAlwaysExportCustomAttributes = () => featureFlags.get(FEATURE_FLAGS.ALWAYS_EXPORT_CUSTOM_ATTRIBUTES_IN_REPORT);
const isGenerateIssuesBim360Report = () => featureFlags.get(FEATURE_FLAGS.GENERATE_ISSUES_BIM360_REPORT);

const rootCauseAndTypeSortFields = ['ng_issue_type', 'ng_issue_subtype', 'root_cause'];
const reportConsts = {
  descending: 'DESC',
  ascending: 'ASC',
  xlsx: 'xlsx',
  excel: 'excel',
  pdf: 'pdf',
  issues: 'issues',
  summary: 'summary',
  detail: 'detail',
  id: 'id',
  customAttributes: 'customAttributes',
  dateParameters: ['dueDate'],
  dateTimeParameters: ['createdAt'],
  issuesBim360: 'issues-bim360'
};

/* eslint-disable camelcase */
export const ReportsConfig = {
  [REPORT_TYPES.SINGLE_QUALITY_ISSUE]: {
    reportDefinitionId: ReportDefinitionId[REPORT_TYPES.SINGLE_QUALITY_ISSUE],
    filters: {
      id: [{name: 'id', value: simpleValue, type: 'filter'}]
    }
  },
  [REPORT_TYPES.QUALITY_ISSUES]: {
    reportDefinitionId: ReportDefinitionId[REPORT_TYPES.QUALITY_ISSUES],
    filters: {
      id: [{name: 'issue-id-filter', value: simpleValue}],
      search: [{name: 'search', value: singleValue}],
      lbsLocation: [{name: 'issue-location-filter', value: singleValue}],
      locationId: [{name: 'issue-location-filter', value: singleValue}],
      lbsSublocations: [{name: 'issue-location-filter-descendants', value: singleValue}],
      subLocationId: [{name: 'issue-location-filter-descendants', value: singleValue}],
      issueType: [{name: 'issue-type-filter', value: simpleValue}],
      issueTypeId: [{name: 'issue-type-filter', value: simpleValue}],
      issueSubType: [{name: 'issue-subtype-filter', value: simpleValue}],
      issueSubtypeId: [{name: 'issue-subtype-filter', value: simpleValue}],
      status: [{name: 'issue-status-filter', value: simpleValue}],
      ownerId: [{name: 'owner', value: simpleValue}],
      owner: [{name: 'owner', value: simpleValue}],
      assignee: [{name: 'issue-assignee-filter', value: simpleValue}],
      assignedTo: [{name: 'issue-assignee-filter', value: simpleValue}],
      createdBy: [{name: 'issue-created-by-filter', value: simpleValue}],
      dueDate: [
        {name: 'due-date-range-start-filter', value: startDateValue},
        {name: 'due-date-range-end-filter', value: endDateValue}
      ],
      createdAt: [
        {name: 'issue-created-at-range-start-filter', value: startInstantValue},
        {name: 'issue-created-at-range-end-filter', value: endInstantValue}
      ],
      rootCauseId: [{name: 'issue-root-cause-filter', value: simpleValue}],
      linkedDocumentUrn: [{name: 'issue-target-urn-filter', value: singleValue}],
      linkedTo: [{name: 'issue-linked-to-filter', value: simpleValue}]
    },
    sort: {
      [getSortName(ISSUE_ATTRIBUTE_NAMES.STATUS)]: 'issue-status-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.TITLE)]: 'issue-title-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.DUE_DATE)]: 'due-date-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.ID)]: 'issue-identifier-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID)]: 'issue-type-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID)]: 'issue-subtype-sort',
      [getSortName(ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID)]: 'issue-root-cause-sort'
    }
  }
};
/* eslint-enable camelcase */

const getLocationParameter = queryParams =>
  queryParams[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS] ? 'subLocationId' : 'locationId';

const parseQueryParams = queryParams => ({
  id: queryParams[ISSUE_HIDDEN_FILTER_PROPERTIES.ID],
  status: queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER],
  assignedTo: queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER],
  owner: queryParams[ISSUE_FILTER_PROPERTIES.OWNER_FILTER],
  createdBy: queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER],
  createdAt: [
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
  ],
  dueDate: [
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
  ],
  rootCauseId: queryParams[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER],
  issueTypeId: queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER],
  issueSubtypeId: queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER],
  search: queryParams[ISSUE_FILTER_PROPERTIES.SEARCH],
  [getLocationParameter(queryParams)]: queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER],
  linkedDocumentUrn: queryParams['linkedDocumentUrn'],
  linkedTo: queryParams[ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER],
  customAttributes: queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]
});

const isFilterSupported = (reportConfig, filterName) => {
  if (
    filterName === 'id' &&
    listReportIds.includes(reportConfig.reportDefinitionId) &&
    !featureFlags.get(FEATURE_FLAGS.FILTER_ISSUES_BY_ID_REPORT)
  ) {
    return false;
  }
  return reportConfig.filters[filterName];
};

const formatDateFilterToString = value => (value[0] !== '' && value[1] !== '' ? `${value[0]}...${value[1]}` : '');

const formatDateFilterToUtcDateTimeString = value =>
  value[0] !== '' && value[1] !== ''
    ? `${formatStartDateUtcWithTimeZone(value[0])}...${formatEndDateUtcWithTimeZone(value[1])}`
    : '';

const constructReportFilterParameters = (reportConfig, queryParams) => {
  const parameters = [];
  if (queryParams) {
    parameters.push(
      ...Object.keys(queryParams)
        .filter(
          filterName =>
            isFilterSupported(reportConfig, filterName) &&
            queryParams[filterName] !== undefined &&
            reportConfig.filters[filterName].length > 0
        )
        .flatMap(filterName => {
          const filterObject = {
            field: filterName,
            value: queryParams[filterName].toString()
          };
          if (reportConsts.dateParameters.includes(filterName)) {
            filterObject.value = formatDateFilterToString(queryParams[filterName]);
          }
          if (reportConsts.dateTimeParameters.includes(filterName)) {
            filterObject.value = formatDateFilterToUtcDateTimeString(queryParams[filterName]);
          }
          return filterObject;
        })
    );
  }
  if (queryParams.customAttributes) {
    const value = JSON.parse(queryParams.customAttributes)
      .map(el =>
        //If it is a dropdown custom attribute, we would have el.value.id
        el.value.id ? `"${el.id}":"${el.value.id}"` : `"${el.id}":"${el.value}"`
      )
      .join(',');
    parameters.push({
      field: reportConsts.customAttributes,
      value: `{${value}}`
    });
  }
  return parameters.filter(
    parameter => parameter.value.length !== 0 && parameter.value[0] !== null && parameter.field !== undefined
  );
};

const constructReportSortParameters = (reportConfig, queryParams) => {
  const sortParameters = [];

  if (queryParams.sort) {
    const isDesc = queryParams.sort[0] === '-';
    const sortParam = isDesc ? queryParams.sort.substring(1) : queryParams.sort;

    if (
      featureFlags.get(FEATURE_FLAGS.SORT_BY_TYPE_AND_ROOT_CAUSE_REPORT) ||
      !rootCauseAndTypeSortFields.includes(sortParam)
    ) {
      const reportSortParam = reportConfig.sort[sortParam];
      if (reportSortParam) {
        const value = isDesc ? [reportConsts.descending] : [reportConsts.ascending];
        sortParameters.push(`${sortParam} ${value}`);
      } else {
        // eslint-disable-next-line no-console
        console.log(`Unknown report sort param for field: ${sortParam}`);
      }
    }
  }
  return sortParameters;
};

export const getListReportDataObject = (title, message, reportConfig, queryParams, format = reportConsts.pdf) => ({
  format: format === reportConsts.xlsx ? reportConsts.excel : reportConsts.pdf,
  service: isGenerateIssuesBim360Report() ? reportConsts.issuesBim360 : reportConsts.issues,
  title,
  message,
  template: reportConsts.summary,
  options: {
    ...(isAlwaysExportCustomAttributes() && {fields: [reportConsts.customAttributes]}),
    filter: constructReportFilterParameters(reportConfig, parseQueryParams(queryParams)),
    sort: constructReportSortParameters(reportConfig, queryParams)
  }
});

export const getSingleReportDataObject = issue => {
  const textIssueType = i18n.t(['issue.label_capitalized', 'issueType.issues.label_capitalized']);
  const title = `${textIssueType} #${issue.displayId}: ${issue.title}`;

  return {
    format: reportConsts.pdf,
    service: isGenerateIssuesBim360Report() ? reportConsts.issuesBim360 : reportConsts.issues,
    title,
    template: reportConsts.detail,
    options: {
      filter: [
        {
          field: reportConsts.id,
          value: issue.id
        }
      ]
    }
  };
};

export const linkedDocumentUrn = (targetUrn, documentVersion) =>
  `${targetUrn}${documentVersion ? `@${documentVersion}` : ''}`;
